import { generateSlug } from '@/lib/utils';

export type ContentTagRef = {
  name: string;
  slug: string;
  /** Harici URL (tag_url). Doluysa içerik linki buraya gider. */
  url?: string | null;
  /** Bilgi paneli / hover için görsel veya açıklama varsa true */
  hasInfo?: boolean;
};

const SKIP_TAGS = new Set([
  'a',
  'script',
  'style',
  'code',
  'pre',
  'iframe',
  'svg',
  'button',
  'textarea',
  'noscript',
  // Başlıklar içinde etiketleme yok (h1–h6)
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'title',
]);

/** URL / domain parçası — bu karakterlerden sonra etiket eşleşmesin */
const URLISH_BEFORE = new Set(['/', '.', '@', ':', '=', '?', '&', '#']);

/** Tek kelime: boşluk / NBSP yok (çok kelimeli etiketler içerikte linklenmez) */
function isSingleWordTag(name: string): boolean {
  const trimmed = name.trim()
  if (trimmed.length < 2) return false
  return !/[\s\u00A0\u202F]/.test(trimmed)
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeTagKey(value: string): string {
  return value.trim().toLocaleLowerCase('tr-TR');
}

export function resolveTagHref(slug: string, tagUrl?: string | null): string {
  const raw = (tagUrl || '').trim();
  if (!raw) return `/haberleri/${encodeURIComponent(slug)}`;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  // Reject protocol-relative and bare hosts without scheme
  if (raw.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(raw)) {
    return `/haberleri/${encodeURIComponent(slug)}`;
  }
  return `https://${raw}`;
}

/** HTML’de bu etiketin hedef href’i zaten varsa “harcanmış” sayılır */
function seedLinkedCounts(
  html: string,
  tags: Array<{ key: string; href: string; slug: string }>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const tag of tags) {
    const hrefEscaped = escapeRegExp(tag.href);
    const hrefRe = new RegExp(
      `<a\\b[^>]*\\bhref=["']${hrefEscaped}(?:["'/#?]|$)`,
      'i',
    );
    if (hrefRe.test(html)) {
      counts.set(tag.key, 1);
      continue;
    }
    const slugRe = escapeRegExp(tag.slug);
    const internalRe = new RegExp(
      `<a\\b[^>]*\\bhref=["'][^"']*/haberleri/${slugRe}(?:["'/#?]|$)`,
      'i',
    );
    if (internalRe.test(html)) {
      counts.set(tag.key, 1);
    }
  }
  return counts;
}

/**
 * Yalnızca düz metin parçalarına uygula; etiket/attribute içine dokunma.
 * Bir etiket eklendikten sonra tekrar tokenize edilir — `/haberleri/...` bozulmaz.
 */
function mapTextChunks(html: string, mapFn: (text: string) => string): string {
  if (!html) return html;
  let out = '';
  const tokenRe = /(<\/?[a-zA-Z][^>]*>)|([^<]+)/g;
  for (const match of html.matchAll(tokenRe)) {
    if (match[1]) out += match[1];
    else if (match[2]) out += mapFn(match[2]);
  }
  return out || html;
}

type PreparedTag = {
  key: string;
  name: string;
  slug: string;
  href: string;
  hasInfo: boolean;
  /** RegExp source (global state yok — her kullanımda yeni RegExp) */
  patternSource: string;
};

function linkOnceInPlainText(
  text: string,
  tag: PreparedTag,
  linkedCount: Map<string, number>,
  maxPerTag: number,
  className: string,
): string {
  const count = linkedCount.get(tag.key) || 0;
  if (count >= maxPerTag || !text) return text;

  const pattern = new RegExp(tag.patternSource, 'giu');

  let replaced = false;
  return text.replace(
    pattern,
    (full, before: string, word: string, offset: number, input: string) => {
      if (replaced) return full;
      if ((linkedCount.get(tag.key) || 0) >= maxPerTag) return full;
      // /haberleri, www.haber.com, mailto: vb. içinde eşleşme
      if (before && URLISH_BEFORE.has(before)) return full;
      // haber.com — kök + TLD (lookahead eşleşmeye dahil değil)
      const after = input.slice(offset + full.length);
      if (/^\.[a-z]{2,}\b/i.test(after)) return full;
      replaced = true;
      linkedCount.set(tag.key, (linkedCount.get(tag.key) || 0) + 1);
      const safeHref = tag.href.replace(/"/g, '&quot;');
      const rel = tag.href.startsWith('http')
        ? ' rel="noopener noreferrer" target="_blank"'
        : '';
      const infoClass = tag.hasInfo ? ' news-inline-tag-has-info' : '';
      const infoData = tag.hasInfo ? ' data-tag-has-info="1"' : '';
      return `${before}<a href="${safeHref}" class="${className}${infoClass}" data-tag-slug="${tag.slug.replace(/"/g, '')}"${infoData}${rel}>${word}</a>`;
    },
  );
}

/**
 * Haber gövdesinde geçen sistem etiketlerini linkler.
 * - Harici tagUrl varsa oraya, yoksa `/haberleri/{slug}`
 * - Zaten <a> / script / code / h1–h6 içindeyse dokunulmaz
 * - Attribute ve URL parçalarına dokunulmaz
 * - Her etiket en fazla 1 kez linklenir
 * - Yalnızca tek kelimelik etiketler (boşluklu adlar atlanır)
 * - Kelime sınırı: boşluk / satır başı-sonu / cümle noktalaması
 *   (Türkiye-AB içindeki AB eşleşmez; HABER≠HABERLER)
 */
export function linkArticleTagsInHtml(
  html: string,
  tags: ContentTagRef[],
  options?: { maxPerTag?: number; className?: string },
): string {
  const maxPerTag = Math.max(1, options?.maxPerTag ?? 1);
  const className = options?.className ?? 'news-inline-tag';

  const byKey = new Map<string, PreparedTag>();
  for (const t of tags) {
    const name = (t.name || '').trim();
    const slug = (t.slug || generateSlug(name)).trim();
    if (!isSingleWordTag(name) || !slug) continue;
    const key = normalizeTagKey(name);
    if (!byKey.has(key)) {
      const href = resolveTagHref(slug, t.url);
      const escaped = escapeRegExp(name);
      // Önce: başlangıç / boşluk / açılış tırnak-parantez
      // Sonra: bitiş / boşluk / cümle noktalaması
      // Tire ile bitişik bileşiklerde (Türkiye-AB) eşleşme yok
      byKey.set(key, {
        key,
        name,
        slug,
        href,
        hasInfo: Boolean(t.hasInfo),
        patternSource: `(^|[\\s\\u00A0\\u202F]|[\"'«(\\[{])(${escaped})(?=$|[\\s\\u00A0\\u202F]|[\"'»)\\]}.,:;!?…])`,
      });
    }
  }

  const cleaned = Array.from(byKey.values()).sort(
    (a, b) => b.name.length - a.name.length,
  );

  if (!html?.trim() || cleaned.length === 0) return html;

  const linkedCount = seedLinkedCounts(html, cleaned);
  const allLinked = () =>
    cleaned.every((t) => (linkedCount.get(t.key) || 0) >= maxPerTag);

  let depthSkip = 0;
  let out = '';
  const tokenRe = /(<\/?[a-zA-Z][^>]*>)|([^<]+)/g;

  for (const match of html.matchAll(tokenRe)) {
    const tagHtml = match[1];
    const text = match[2];

    if (tagHtml) {
      const nameMatch = /^<\/?\s*([a-zA-Z0-9:-]+)/.exec(tagHtml);
      const name = (nameMatch?.[1] || '').toLowerCase();
      const isClose = tagHtml.startsWith('</');
      const isSelf =
        /\/>$/.test(tagHtml) ||
        /^(br|img|hr|input|meta|link|source|wbr)$/i.test(name);

      if (SKIP_TAGS.has(name) && !isSelf) {
        if (isClose) depthSkip = Math.max(0, depthSkip - 1);
        else depthSkip += 1;
      }
      out += tagHtml;
      continue;
    }

    if (!text || depthSkip > 0 || allLinked()) {
      out += text || '';
      continue;
    }

    // İlk eşleşmeye kadar düz metin; HTML eklendikten sonra tokenize et
    let segment = text;
    let hasInsertedHtml = false;
    for (const tag of cleaned) {
      if ((linkedCount.get(tag.key) || 0) >= maxPerTag) continue;
      if (hasInsertedHtml) {
        segment = mapTextChunks(segment, (chunk) =>
          linkOnceInPlainText(chunk, tag, linkedCount, maxPerTag, className),
        );
      } else {
        const next = linkOnceInPlainText(
          segment,
          tag,
          linkedCount,
          maxPerTag,
          className,
        );
        if (next !== segment) {
          hasInsertedHtml = true;
          segment = next;
        }
      }
    }
    out += segment;
  }

  return out || html;
}

/** AI / serbest metinden gelen etiket adlarını yalnızca sistemde var olanlara indirger */
export function filterToExistingTagNames(
  candidates: string[],
  existingTagNames: string[],
): string[] {
  if (!candidates.length || !existingTagNames.length) return [];
  const byKey = new Map(
    existingTagNames
      .map((name) => name.trim())
      .filter(Boolean)
      .map((name) => [normalizeTagKey(name), name] as const),
  );
  const out: string[] = [];
  const seen = new Set<string>();
  // En uzun aday önce — kısa etiket yanlış seçilmesin
  const ordered = [...candidates].sort(
    (a, b) => String(b).trim().length - String(a).trim().length,
  );
  for (const raw of ordered) {
    const key = normalizeTagKey(raw);
    const canonical = byKey.get(key);
    if (!canonical || seen.has(key)) continue;
    seen.add(key);
    out.push(canonical);
  }
  return out;
}

/**
 * Otomatik metin eşlemesinde zayıf/genel etiketler (içerikte rastgele geçer).
 * Elle seçime engel değil — yalnızca auto-suggest.
 */
const WEAK_AUTO_TAG_KEYS = new Set(
  [
    'haber',
    'son dakika',
    'video',
    'fotoğraf',
    'foto',
    'gündem',
    'magazin',
    'iletişim',
    'hafıza',
    'gelir',
    'döner',
    'zaman',
    'kadar',
    'ne kadar',
    'ne zaman',
    'nasıl',
    'neden',
    'niçin',
    'nerede',
    'kimdir',
    'kaç',
    'var',
    'yok',
    'yeni',
    'son',
    'ilk',
    'büyük',
    'küçük',
    'gün',
    'yıl',
    'ay',
    'hafta',
    'bugün',
    'yarın',
    'dün',
  ].map((s) => s.toLocaleLowerCase('tr-TR')),
);

/** Otomatik eklenmeye uygun mu? (kişi/yer/olay adı vb.) */
export function isEligibleAutoTagName(name: string): boolean {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (!trimmed) return false;
  const key = normalizeTagKey(trimmed);
  if (WEAK_AUTO_TAG_KEYS.has(key)) return false;
  // Soru / kalıp etiketleri
  if (/^(ne|nasıl|neden|niçin|kim|kaç|hangi)\b/i.test(trimmed)) return false;

  const words = trimmed.split(' ').filter(Boolean);
  if (words.length === 1) {
    // Tek kelime: en az 6 harf (kısa genel kelimeleri ele)
    if (trimmed.length < 6) return false;
  } else {
    // Çok kelime: toplam en az 5 (örn. "Ata Demirer")
    if (trimmed.length < 5) return false;
  }
  return true;
}

/**
 * Başlık/özet metninde geçen mevcut site etiketlerini bulur (en uzun önce).
 * Kısa/genel kelimeler elenir — tam içerik taranmamalı.
 */
export function matchExistingTagsInText(
  text: string,
  existingTagNames: string[],
  limit = 6,
): string[] {
  const plain = String(text || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain || !existingTagNames.length) return [];

  // Uzun gövde metni rastgele eşleşme üretir — ilk ~400 karakter (başlık+özet)
  const haySource = plain.length > 400 ? plain.slice(0, 400) : plain;
  const hay = haySource.toLocaleLowerCase('tr-TR');
  const ordered = [...existingTagNames]
    .map((n) => n.trim())
    .filter((n) => isEligibleAutoTagName(n))
    .sort((a, b) => b.length - a.length);

  const isBound = (ch: string | undefined) =>
    !ch || !/[\p{L}\p{N}_]/u.test(ch);

  const out: string[] = [];
  const seen = new Set<string>();

  for (const name of ordered) {
    const key = normalizeTagKey(name);
    if (seen.has(key)) continue;
    const needle = name.toLocaleLowerCase('tr-TR');
    let from = 0;
    while (from <= hay.length - needle.length) {
      const at = hay.indexOf(needle, from);
      if (at < 0) break;
      const before = at === 0 ? ' ' : hay[at - 1];
      const after =
        at + needle.length >= hay.length ? ' ' : hay[at + needle.length];
      if (isBound(before) && isBound(after)) {
        seen.add(key);
        out.push(name);
        break;
      }
      from = at + 1;
    }
    if (out.length >= limit) break;
  }
  return out;
}

/** Aday + metin eşleşmesi; mevcut seçimi koruyarak birleştirir */
export function mergeAiSuggestedTags(opts: {
  selected: string[];
  aiCandidates?: string[];
  /** Tercihen yalnızca başlık + alt başlık (tam HTML içerik değil) */
  text?: string;
  existingTagNames: string[];
  limit?: number;
}): { tags: string[]; added: string[] } {
  const limit = opts.limit ?? 8;
  const known = opts.existingTagNames;
  // AI adayları: sistemde varsa al; zayıf genel etiketleri yine de ele
  const fromAi = filterToExistingTagNames(opts.aiCandidates || [], known).filter(
    (n) => isEligibleAutoTagName(n) || n.trim().split(/\s+/).length >= 2,
  );
  const fromText = matchExistingTagsInText(opts.text || '', known, Math.min(6, limit));
  const seen = new Set<string>();
  const tags: string[] = [];
  const added: string[] = [];
  const selectedKeys = new Set(
    opts.selected.map((t) => normalizeTagKey(t)).filter(Boolean),
  );

  for (const name of [...opts.selected, ...fromAi, ...fromText]) {
    const trimmed = name.trim();
    if (!trimmed) continue;
    const key = normalizeTagKey(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    const canonical =
      known.find((k) => normalizeTagKey(k) === key) || trimmed;
    tags.push(canonical);
    if (!selectedKeys.has(key)) added.push(canonical);
    if (tags.length >= limit) break;
  }
  return { tags, added };
}

/** URL import: sistemde olanlar seçili; olmayanlar + ile eklenebilir */
export function partitionImportTags(opts: {
  candidates: string[];
  existingTagNames: string[];
  limit?: number;
}): { known: string[]; unknown: string[] } {
  const limit = opts.limit ?? 12;
  const known = filterToExistingTagNames(
    opts.candidates,
    opts.existingTagNames,
  )
    .filter(
      (n) => isEligibleAutoTagName(n) || n.trim().split(/\s+/).length >= 2,
    )
    .slice(0, limit);
  const knownKeys = new Set(known.map((n) => normalizeTagKey(n)));
  const unknown: string[] = [];
  const seen = new Set<string>();
  for (const raw of opts.candidates) {
    const trimmed = String(raw || '')
      .trim()
      .replace(/\s+/g, ' ');
    if (!trimmed) continue;
    const key = normalizeTagKey(trimmed);
    if (!key || knownKeys.has(key) || seen.has(key)) continue;
    if (!isEligibleAutoTagName(trimmed) && trimmed.split(/\s+/).length < 2) {
      continue;
    }
    seen.add(key);
    unknown.push(trimmed);
    if (unknown.length >= limit) break;
  }
  return { known, unknown };
}

export function tagsFromSelectedNames(names: string[]): ContentTagRef[] {
  return names
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ name, slug: generateSlug(name) }));
}
